<template>
  <v-container fluid class="pa-4 rtl">
    <!-- Header -->
    <v-card class="header-card mb-4" elevation="2">
      <div class="d-flex align-center justify-space-between flex-wrap ga-3">
        <div>
          <div class="text-h5 font-weight-bold" style="color: #4a4f55">
            خدمات المرفق الصحي
          </div>
          <div class="text-body-2 text-grey-darken-1 mt-1">
            الكهرباء – المياه – الإصحاح البيئي – إدارة المخلفات
          </div>
        </div>
      </div>
    </v-card>

    <!-- KPIs -->
    <v-row class="mb-4">
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card primary">
          <div class="kpi-value">{{ avgElectricityHours }}</div>
          <div class="kpi-label">متوسط ساعات توفر الكهرباء</div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card warning">
          <div class="kpi-value">{{ avgWaterHours }}</div>
          <div class="kpi-label">متوسط ساعات توفر المياه</div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card success">
          <div class="kpi-value">{{ safeElectricFacilities }}</div>
          <div class="kpi-label">مرافق آمنة كهربائياً</div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card danger">
          <div class="kpi-value">{{ totalRehabCost.toLocaleString() }}</div>
          <div class="kpi-label">إجمالي تكلفة التأهيل</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Charts -->
    <v-row class="mb-4">
      <v-col cols="12" md="6">
        <v-card class="chart-card">
          <div class="table-header">المخاطر الكهربائية</div>
          <apexchart
            v-if="electricHazardsSeries.length"
            type="donut"
            height="310"
            :series="electricHazardsSeries"
            :options="donutOptions(electricHazardsLabels)"
          />
          <div v-else class="empty-chart">لا توجد بيانات</div>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card class="chart-card">
          <div class="table-header">وسائل الصرف الصحي المتوفرة</div>
          <apexchart
            v-if="sanitationTypesSeries.length"
            type="donut"
            height="310"
            :series="sanitationTypesSeries"
            :options="donutOptions(sanitationTypesLabels)"
          />
          <div v-else class="empty-chart">لا توجد بيانات</div>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card class="chart-card">
          <div class="table-header">وصول دورات المياه لذوي الإعاقة</div>
          <apexchart
            v-if="toiletAccessibleSeries.length"
            type="pie"
            height="310"
            :series="toiletAccessibleSeries"
            :options="donutOptions(toiletAccessibleLabels)"
          />
          <div v-else class="empty-chart">لا توجد بيانات</div>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card class="chart-card">
          <div class="table-header">نوع الحاجة إلى إعادة التأهيل</div>
          <apexchart
            v-if="rehabNeedSeries?.[0]?.data?.length"
            type="bar"
            height="310"
            :series="rehabNeedSeries"
            :options="barOptions(rehabNeedLabels)"
          />
          <div v-else class="empty-chart">لا توجد بيانات</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filters -->
    <v-card class="mb-4 pa-4 filter-card" elevation="1">
      <v-row>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="filters.search"
            label="بحث برقم الاستبيان"
            density="compact"
            variant="outlined"
            clearable
            hide-details
          />
        </v-col>

        <v-col cols="12" md="4">
          <v-select
            v-model="filters.electricSource"
            :items="electricSourceItems"
            label="مصدر الكهرباء"
            density="compact"
            variant="outlined"
            clearable
            hide-details
          />
        </v-col>

        <v-col cols="12" md="4">
          <v-select
            v-model="filters.waterSource"
            :items="waterSourceItems"
            label="مصدر المياه"
            density="compact"
            variant="outlined"
            clearable
            hide-details
          />
        </v-col>
      </v-row>
    </v-card>

    <!-- Table -->
    <v-card elevation="1" class="table-card">
      <div class="table-header">
        حالة خدمات المرفق الصحي
        <span>({{ filteredUtilities.length }})</span>
      </div>

      <v-table class="custom-table">
        <thead>
          <tr>
            <th>المرفق / الاستبيان</th>
            <th>مصدر الكهرباء</th>
            <th>ساعات الكهرباء</th>
            <th>مصدر المياه</th>
            <th>ساعات المياه</th>
            <th>الصرف الصحي</th>
            <th>دورات مياه لذوي الإعاقة</th>
            <th>المخاطر الكهربائية</th>
            <th>طريقة المخلفات</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="item in paginatedUtilities" :key="item.id">
            <td class="font-weight-bold">استبيان #{{ item.survey_id }}</td>

            <td>
              <v-chip :color="chipColor(item.elec_main_source)" size="small">
                {{ item.elec_main_source || "غير محدد" }}
              </v-chip>
            </td>

            <td>{{ item.elec_availability_hrs ?? 0 }} ساعة</td>

            <td>{{ item.water_main_source || "غير محدد" }}</td>

            <td>{{ item.water_availability_hrs ?? 0 }} ساعة</td>

            <td>{{ item.sanitation_types || "غير محدد" }}</td>

            <td>
              <v-chip
                :color="item.toilet_pwds_accessible === 'نعم' ? 'green' : 'red'"
                size="small"
              >
                {{ item.toilet_pwds_accessible || "غير محدد" }}
              </v-chip>
            </td>

            <td>
              <v-chip
                :color="
                  !item.elec_hazards || item.elec_hazards === 'none'
                    ? 'green'
                    : 'red'
                "
                size="small"
              >
                {{ item.elec_hazards || "none" }}
              </v-chip>
            </td>

            <td>{{ item.waste_disposal_method || "غير محدد" }}</td>
          </tr>

          <tr v-if="!paginatedUtilities.length">
            <td colspan="9" class="text-center py-6">لا توجد بيانات مطابقة</td>
          </tr>
        </tbody>
      </v-table>

      <div
        class="d-flex align-center justify-space-between pa-4 flex-wrap ga-3"
      >
        <div>
          عرض {{ paginatedUtilities.length }} من {{ filteredUtilities.length }}
        </div>

        <div class="d-flex align-center">
          <v-select
            v-model="itemsPerPage"
            :items="[5, 10, 20, 50]"
            label="الصفوف"
            density="compact"
            variant="outlined"
            hide-details
            style="width: 120px"
          />

          <v-pagination
            v-model="currentPage"
            :length="pageCount"
            total-visible="7"
            class="ms-4"
          />
        </div>
      </div>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import api from "@/plugins/api";

const utilities = ref([]);
const rehabCosts = ref([]);
const loading = ref(false);

const filters = ref({
  search: "",
  electricSource: null,
  waterSource: null,
});

const currentPage = ref(1);
const itemsPerPage = ref(10);

const fetchData = async () => {
  try {
    loading.value = true;

    const [utilitiesRes, rehabRes] = await Promise.all([
      api.get("/facility-utilities"),
      api.get("/cost-rehab-priorities"),
    ]);

    utilities.value = Array.isArray(utilitiesRes.data)
      ? utilitiesRes.data
      : Array.isArray(utilitiesRes.data?.data)
      ? utilitiesRes.data.data
      : [];

    rehabCosts.value = Array.isArray(rehabRes.data)
      ? rehabRes.data
      : Array.isArray(rehabRes.data?.data)
      ? rehabRes.data.data
      : [];
  } catch (error) {
    console.error(error);
    utilities.value = [];
    rehabCosts.value = [];
  } finally {
    loading.value = false;
  }
};

onMounted(fetchData);

const average = (items, field) => {
  if (!items.length) return 0;

  const total = items.reduce((sum, item) => {
    return sum + Number(item[field] || 0);
  }, 0);

  return (total / items.length).toFixed(1);
};

const avgElectricityHours = computed(() =>
  average(utilities.value, "elec_availability_hrs")
);

const avgWaterHours = computed(() =>
  average(utilities.value, "water_availability_hrs")
);

const safeElectricFacilities = computed(
  () =>
    utilities.value.filter(
      (item) => !item.elec_hazards || item.elec_hazards === "none"
    ).length
);

const totalRehabCost = computed(() =>
  rehabCosts.value.reduce(
    (sum, item) => sum + Number(item.estimated_rehab_cost || 0),
    0
  )
);

const countByField = (items, field) => {
  const result = {};

  items.forEach((item) => {
    const value = item[field] || "غير محدد";
    result[value] = (result[value] || 0) + 1;
  });

  return result;
};

const electricHazardsData = computed(() =>
  countByField(utilities.value, "elec_hazards")
);

const sanitationTypesData = computed(() =>
  countByField(utilities.value, "sanitation_types")
);

const toiletAccessibleData = computed(() =>
  countByField(utilities.value, "toilet_pwds_accessible")
);

const rehabNeedData = computed(() =>
  countByField(rehabCosts.value, "type_rehab_need")
);

const electricHazardsLabels = computed(() =>
  Object.keys(electricHazardsData.value)
);

const electricHazardsSeries = computed(() =>
  Object.values(electricHazardsData.value)
);

const sanitationTypesLabels = computed(() =>
  Object.keys(sanitationTypesData.value)
);

const sanitationTypesSeries = computed(() =>
  Object.values(sanitationTypesData.value)
);

const toiletAccessibleLabels = computed(() =>
  Object.keys(toiletAccessibleData.value)
);

const toiletAccessibleSeries = computed(() =>
  Object.values(toiletAccessibleData.value)
);

const rehabNeedLabels = computed(() => Object.keys(rehabNeedData.value));

const rehabNeedSeries = computed(() => [
  {
    name: "عدد الاحتياجات",
    data: Object.values(rehabNeedData.value),
  },
]);

const donutOptions = (labels) => ({
  labels,
  chart: {
    type: "donut",
  },
  legend: {
    position: "bottom",
  },
  dataLabels: {
    enabled: true,
  },
});

const barOptions = (labels) => ({
  chart: {
    type: "bar",
    toolbar: {
      show: false,
    },
  },
  xaxis: {
    categories: labels,
    labels: {
      rotate: -45,
    },
  },
  plotOptions: {
    bar: {
      borderRadius: 6,
      horizontal: false,
    },
  },
});

const chipColor = (value) => {
  if (!value) return "grey";

  if (["شبكة", "none", "نعم", "آمن", "كامل"].includes(value)) {
    return "green";
  }

  if (["مولد", "جزئي", "متوسط"].includes(value)) {
    return "orange";
  }

  if (["لا يوجد", "متوقف", "لا", "غير آمن"].includes(value)) {
    return "red";
  }

  return "blue-grey";
};

const electricSourceItems = computed(() => [
  ...new Set(utilities.value.map((x) => x.elec_main_source).filter(Boolean)),
]);

const waterSourceItems = computed(() => [
  ...new Set(utilities.value.map((x) => x.water_main_source).filter(Boolean)),
]);

const filteredUtilities = computed(() => {
  return utilities.value.filter((item) => {
    const search = filters.value.search?.trim();

    if (search && !String(item.survey_id || "").includes(search)) {
      return false;
    }

    if (
      filters.value.electricSource &&
      item.elec_main_source !== filters.value.electricSource
    ) {
      return false;
    }

    if (
      filters.value.waterSource &&
      item.water_main_source !== filters.value.waterSource
    ) {
      return false;
    }

    return true;
  });
});

const pageCount = computed(() =>
  Math.max(1, Math.ceil(filteredUtilities.value.length / itemsPerPage.value))
);

const paginatedUtilities = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  return filteredUtilities.value.slice(start, start + itemsPerPage.value);
});

watch(
  () => [
    filters.value.search,
    filters.value.electricSource,
    filters.value.waterSource,
    itemsPerPage.value,
  ],
  () => {
    currentPage.value = 1;
  }
);
</script>

<style scoped>
.rtl {
  direction: rtl;
}

.header-card {
  border-right: 5px solid #4a4f55;
  border-radius: 18px;
  padding: 20px;
}

.kpi-card {
  border-radius: 18px;
  padding: 20px;
  text-align: center;
  min-height: 125px;
}

.kpi-value {
  font-size: 32px;
  font-weight: 800;
}

.kpi-label {
  margin-top: 6px;
  font-size: 13px;
}

.danger {
  background: #fdecea;
  color: #c0392b;
}

.warning {
  background: #fff4e5;
  color: #d4821a;
}

.primary {
  background: #f0f1f2;
  color: #4a4f55;
}

.success {
  background: #eafaf1;
  color: #1e8449;
}

.chart-card,
.table-card,
.filter-card {
  border-radius: 18px;
  overflow: hidden;
}

.table-header {
  padding: 18px;
  font-size: 18px;
  font-weight: 700;
  color: #3a3f47;
  border-bottom: 1px solid #eee;
}

.table-header span {
  color: #6b7280;
  font-size: 14px;
}

.empty-chart {
  height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #777;
}

.custom-table {
  direction: rtl;
}

:deep(th) {
  font-weight: 800 !important;
  color: #3a3f47 !important;
  white-space: nowrap;
}

:deep(td) {
  font-size: 14px;
  white-space: nowrap;
}
</style>
